import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    TextField,
    Autocomplete,
    Typography,
    Grid,
    CircularProgress
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { addressApi, AddressSuggestion } from '../utils/api';

const MIN_CHARS = 2;
const SEARCH_LIMIT = 5;

/** Layers for Google-Maps-style search: both addresses and places in one request. */
const AUTOCOMPLETE_LAYERS = 'address,place';

interface AddressAutocompleteProps {
    value: string;
    onChange: (address: string) => void;
    /** Called when user selects a suggestion (with coords) or leaves free text (lat/lng null). */
    onSelect?: (address: string, lat: number | null, lng: number | null) => void;
    label?: string;
    placeholder?: string;
    error?: boolean;
    helperText?: string;
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
    value,
    onChange,
    onSelect,
    label = "Location",
    placeholder = "Search or enter any address",
    error = false,
    helperText,
}) => {
    const [inputValue, setInputValue] = useState(value);
    const [options, setOptions] = useState<AddressSuggestion[]>([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [fetchError, setFetchError] = useState<string | null>(null);
    const [nearCoords, setNearCoords] = useState<{ lat: number; lng: number } | null>(null);
    const [locationError, setLocationError] = useState<string | null>(null);
    const debounceTimer = useRef<NodeJS.Timeout | null>(null);

    // Get user location for "places near me"
    useEffect(() => {
        if (typeof navigator === 'undefined' || !navigator.geolocation) return;
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setNearCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
                setLocationError(null);
            },
            () => {
                setLocationError(null); // Fail silently; search still works with IP bias
            },
            { timeout: 5000, maximumAge: 300000 }
        );
    }, []);

    // Sync local state with parent value if it changes externally
    useEffect(() => {
        setInputValue(value);
    }, [value]);

    // Google-Maps-style: one search returns both addresses and places (Radar autocomplete)
    const fetchSuggestions = useCallback(async (input: string) => {
        const trimmed = input.trim();
        if (!trimmed || trimmed.length < MIN_CHARS) {
            setOptions([]);
            setFetchError(null);
            return;
        }

        setLoading(true);
        setFetchError(null);
        try {
            const opts: { limit: number; layers: string; country: string; near?: string } = {
                limit: SEARCH_LIMIT,
                layers: AUTOCOMPLETE_LAYERS,
                country: 'US',
            };
            if (nearCoords) {
                opts.near = `${nearCoords.lat},${nearCoords.lng}`;
            }
            const data = await addressApi.autocomplete(trimmed, opts);
            setOptions(data.suggestions || []);
        } catch (err: any) {
            const message = err?.response?.data?.detail
                || err?.message
                || 'Search failed';
            console.error('Address/place search error:', message);
            setFetchError(message);
            setOptions([]);
        } finally {
            setLoading(false);
        }
    }, [nearCoords]);

    // Debounced fetch (300ms)
    const debouncedFetch = useCallback((input: string) => {
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }
        debounceTimer.current = setTimeout(() => {
            fetchSuggestions(input);
        }, 300);
    }, [fetchSuggestions]);

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, []);

    const handleInputChange = (_event: React.SyntheticEvent, newInputValue: string, reason: string) => {
        setInputValue(newInputValue);
        setFetchError(null);
        onChange(newInputValue);
        if (reason === 'input') {
            debouncedFetch(newInputValue);
        }
    };

    const handleOptionSelect = (_event: React.SyntheticEvent, newValue: AddressSuggestion | string | null) => {
        if (!newValue) return;

        if (typeof newValue === 'string') {
            onChange(newValue);
            if (onSelect) onSelect(newValue, null, null);
        } else {
            const address = newValue.formattedAddress;
            const displayText = newValue.placeLabel || address;
            setInputValue(displayText);
            onChange(address);
            if (onSelect) {
                onSelect(address, newValue.latitude, newValue.longitude);
            }
        }
        setOpen(false);
    };

    // On blur with free text (no matching suggestion), notify parent so lat/lng can be cleared
    const handleBlur = useCallback(() => {
        const trimmed = inputValue.trim();
        if (!trimmed || !onSelect) return;
        const matchesOption = options.some(
            (opt) => getOptionLabel(opt) === trimmed
        );
        if (!matchesOption) {
            onSelect(trimmed, null, null);
        }
    }, [inputValue, options, onSelect]);

    // Input display: address → address; place → 店家名字
    const getOptionLabel = (option: AddressSuggestion | string): string => {
        if (typeof option === 'string') return option;
        return option.placeLabel || option.formattedAddress || '';
    };

    // Dropdown: address → main=地址; place → main=店家名字, second line=detail address
    const getDisplayParts = (suggestion: AddressSuggestion) => {
        const isPlace = !!(suggestion.placeLabel || suggestion.layer === 'place');
        const addr = (suggestion.formattedAddress || '').trim();
        if (isPlace) {
            return {
                mainText: suggestion.placeLabel || suggestion.formattedAddress || '',
                secondaryText: addr && addr !== suggestion.placeLabel ? addr : '',
            };
        }
        return { mainText: addr, secondaryText: '' };
    };

    const noResultsHint =
        !loading &&
        inputValue.trim().length >= MIN_CHARS &&
        options.length === 0
            ? 'No matches — your entry will be used as the address.'
            : null;
    const effectiveHelperText =
        fetchError || locationError || noResultsHint || helperText;

    return (
        <Autocomplete
            freeSolo
            fullWidth
            open={open}
            onOpen={() => {
                if (inputValue.trim().length >= MIN_CHARS) setOpen(true);
            }}
            onClose={() => setOpen(false)}
            isOptionEqualToValue={(option, value) => {
                if (typeof option === 'string' || typeof value === 'string') return false;
                return option.formattedAddress === value.formattedAddress;
            }}
            getOptionLabel={getOptionLabel}
            filterOptions={(x) => x} // Disable built-in filtering since API does it
            options={options}
            loading={loading}
            value={inputValue}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            onChange={handleOptionSelect}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    fullWidth
                    error={error || !!fetchError}
                    helperText={effectiveHelperText}
                    InputProps={{
                        ...params.InputProps,
                        onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                            const prev = params.InputProps as { onBlur?: React.FocusEventHandler };
                            prev.onBlur?.(e);
                            handleBlur();
                        },
                        endAdornment: (
                            <React.Fragment>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        ),
                    }}
                />
            )}
            renderOption={(props, option) => {
                if (typeof option === 'string') return null;

                const { mainText, secondaryText } = getDisplayParts(option);

                return (
                    <li {...props}>
                        <Grid container alignItems="center">
                            <Grid item sx={{ display: 'flex', width: 44 }}>
                                <LocationOnIcon sx={{ color: 'text.secondary' }} />
                            </Grid>
                            <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    {mainText}
                                </Typography>
                                {secondaryText && (
                                    <Typography variant="body2" color="text.secondary">
                                        {secondaryText}
                                    </Typography>
                                )}
                            </Grid>
                        </Grid>
                    </li>
                );
            }}
        />
    );
};

export default AddressAutocomplete;
